<script lang="ts" setup>
import { ref, toRefs, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { Filter, SearchQuery } from '../../types';
import { getQueryObject } from '../../query-formatters';
import useSearch from '../../use-search';
import useAggregation from '../../use-aggregation';
import { SubscriptionTypeFilter } from '../../content-filters';
import { useRoute } from 'vue-router';
import { useDisplay } from 'vuetify';
import useLocale from '../../composables/useLocale';
import SelectedFilters from './SelectedFilters.vue';
import { useSearchFilterStore } from '../../use-search-filter';
import useSortOptions, { SortKey, SortOption } from '../use-sort-options';
import { Field } from '../../search-panel-types';
import { Capacitor } from '@capacitor/core';
import SearchInput from './SearchInput.vue';

type Emits = {
  (e: 'toggleFilter'): void;
  (e: 'onSearch', properties: Record<string, any>): void;
};
type Props = {
  contentType: string;
  focused?: boolean;
  enableSearchQuery?: boolean;
  filterFields: Field[];
  sortFields?: SortKey[];
  filters?: Filter;
  label?: string;
  limit?: number;
  selectedSortOption?: string;
  filtersAlias?: string;
  topMenuHeight?: string | number;
  searchSubscription: string[] | string;
  estimatedNrGridCells: number;
  maxFilterCriteriaCount?: number;
  minAge?: number;
  maxAge?: number;
};
const emit = defineEmits<Emits>();
const props = withDefaults(defineProps<Props>(), {
  focused: true,
  enableSearchQuery: true,
  filterFields: undefined,
  sortFields: undefined,
  filters: undefined,
  label: undefined,
  limit: undefined,
  selectedSortOption: undefined,
  filtersAlias: undefined,
  maxFilterCriteriaCount: 1000,
  dynamicTopPadding: undefined,
});

const { contentType } = toRefs(props);
const searchFilterStore = useSearchFilterStore();
const searchStore = useSearch();
const aggregateStore = useAggregation();
const { search, clear } = searchStore;
const { t } = useLocale();
const { isLoading, total } = storeToRefs(searchStore);
const { setFields, getFormattedFilters, getFieldsToArrange, selectedFilters, applyFiltersFromQuery } =
  searchFilterStore;
const { aggregate } = aggregateStore;
const sortOptions = useSortOptions(props.sortFields);

const route = useRoute();
const { mdAndUp, smAndDown } = useDisplay();
const context = ref<SearchQuery>({ q: '' });
const filterDefinitions = ref({});
const selectedSortOption = ref<SortOption>(
  sortOptions.value.find((s) => s.key === props.selectedSortOption) ?? sortOptions.value[0],
);

context.value = Object.keys(route.query).length > 0 ? getQueryObject(window.location.search.slice(1)) : { q: '' };
applyFiltersFromQuery(context.value);
setFields(props.filterFields, props.filtersAlias);

const groupFilteringMode = 'and';

const handleSearch = (query: SearchQuery) => {
  search(
    contentType.value,
    query?.q || '',
    {
      ...getFormattedFilters(props.minAge, props.maxAge),
      ...SubscriptionTypeFilter(props.searchSubscription),
      ...(props.filters ? props.filters : {}),
    },
    props.limit ? props.limit : props.estimatedNrGridCells,
    selectedSortOption.value && selectedSortOption.value.sort ? [selectedSortOption.value.sort] : [],
    groupFilteringMode,
  );
  aggregate(
    contentType.value,
    getFieldsToArrange(),
    {
      ...getFormattedFilters(props.minAge, props.maxAge),
      ...SubscriptionTypeFilter(props.searchSubscription),
      ...(props.filters ? props.filters : {}),
    },
    false,
    props.maxFilterCriteriaCount,
    query.q,
    groupFilteringMode,
  );
  emit('onSearch', {
    contentType: contentType.value,
    query: query.q ?? '',
    filter: {
      ...getFormattedFilters(),
      ...SubscriptionTypeFilter(props.searchSubscription),
      ...(props.filters ? props.filters : {}),
    },
    limit: props.limit ? props.limit : props.estimatedNrGridCells,
    sort: [selectedSortOption.value ? selectedSortOption.value.sort : ''],
  });
};

const handleSort = (sortOption: SortOption) => {
  selectedSortOption.value = sortOption;
};

watch([selectedFilters, selectedSortOption], () => {
  handleSearch(context.value);
});

// Expose function to parent
defineExpose({ handleSearch, handleSort });

const handleClear = () => {
  clear();
  handleSearch(context.value);
};
</script>

<template>
  <div
    class="search-header py-md-6 py-lg-6 py-xl-8 py-xxl-9 pb-4 bg-surface-container"
    :class="Capacitor.isNativePlatform() || smAndDown ? 'pt-10' : 'py-4'"
    :style="topMenuHeight ? { top: topMenuHeight } : {}"
  >
    <div class="bg-surface-container">
      <div class="d-flex">
        <SearchInput
          :filter-definitions="filterDefinitions"
          :placeholder="label ? label : t('content_search.label_search_bar')"
          :enable-search-query="enableSearchQuery"
          density="compact"
          rounded
          hide-details
          :focused="props.focused"
          @submit="handleSearch"
          @reset="handleClear"
        />
        <!-- Filter toggle buttons -->
        <!-- Visible only md+ -->
        <v-btn
          color="primary"
          variant="flat"
          size="large"
          rounded
          class="filter-btn text-on-primary ml-4 d-none d-md-flex"
          @click="emit('toggleFilter')"
        >
          {{ t('content_search.label_search_filters') }}
          <v-icon aria-hidden="true">mdi-filter-variant</v-icon>
        </v-btn>
        <!-- Visible only xs, sm -->
        <v-btn
          icon="mdi-filter-variant"
          color="primary"
          variant="flat"
          large="48"
          class="filter-btn text-on-primary d-flex d-md-none ml-4"
          :aria-label="t('content_search.label_search_filters')"
          @click="emit('toggleFilter')"
        />
        <!-- END Filter toggle buttons -->
      </div>

      <SelectedFilters
        :class="{
          'pt-2': !mdAndUp,
          'pt-4': mdAndUp,
        }"
        :sort-options="sortOptions"
        :total-hits="total"
        :is-loading="isLoading"
        :selected-sort-option="props.selectedSortOption"
        @change-sort="handleSort"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
.search-header {
  position: sticky;
  z-index: 999;
  transition: padding 0.2s ease-in-out;
}
.v-navigation-drawer {
  height: 100vh !important;
}
</style>
